import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiRequest } from '../config/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { useDataCache } from '../context/DataCacheContext';

export default function useFormList() {
  const { getCachedData, setCachedData } = useDataCache();
  const cached = getCachedData('forms');
  const [forms, setForms] = useState(cached || []);
  const [loading, setLoading] = useState(!cached);
  const [formAnalytics, setFormAnalytics] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newFormTitle, setNewFormTitle] = useState('');
  const [newFormDescription, setNewFormDescription] = useState('');
  const [copiedFormId, setCopiedFormId] = useState(null);
  const [openShareDropdown, setOpenShareDropdown] = useState(null);
  const [openActionsDropdown, setOpenActionsDropdown] = useState(null);
  const [highlightedFormId, setHighlightedFormId] = useState(null);
  const { showSuccess, showError } = useToast();
  const { organization } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const shareDropdownRefs = useRef({});
  const actionsDropdownRefs = useRef({});

  // Folder state — currentFolderId is derived from the URL ?folder= param
  const [folders, setFolders] = useState([]);
  const currentFolderId = searchParams.get('folder') || null; // null = root
  const [breadcrumb, setBreadcrumb] = useState([]); // [{id, name}, ...]


  const fetchForms = useCallback(async (folderId = null) => {
    try {
      const folderParam = folderId ? `?folder_id=${folderId}` : '?folder_id=root';
      const response = await apiRequest(`/forms/${folderParam}`);
      if (response.ok) {
        const data = await response.json();
        const formsList = Array.isArray(data) ? data : [];
        setForms(formsList);
        setCachedData('forms', formsList);
      } else {
        console.error('Failed to fetch forms:', response.status);
        setForms([]);
      }
    } catch (error) {
      console.error('Error fetching forms:', error);
      setForms([]);
    } finally {
      setLoading(false);
    }
  }, [setCachedData]);

  const fetchFolders = useCallback(async (parentId = null) => {
    try {
      const parentParam = parentId ? `?parent_id=${parentId}` : '?parent_id=root';
      const response = await apiRequest(`/form-folders/${parentParam}`);
      if (response.ok) {
        const data = await response.json();
        setFolders(Array.isArray(data) ? data : []);
      } else {
        setFolders([]);
      }
    } catch (error) {
      console.error('Error fetching folders:', error);
      setFolders([]);
    }
  }, []);

  const fetchBreadcrumb = useCallback(async (folderId) => {
    if (!folderId) {
      setBreadcrumb([]);
      return;
    }
    try {
      const response = await apiRequest(`/form-folders/${folderId}/breadcrumb`);
      if (response.ok) {
        const data = await response.json();
        setBreadcrumb(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching breadcrumb:', error);
    }
  }, []);

  // Navigate into a folder — update the URL ?folder= param so state is preserved on refresh
  const navigateToFolder = useCallback((folderId) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (folderId) {
        next.set('folder', folderId);
      } else {
        next.delete('folder');
      }
      return next;
    });
  }, [setSearchParams]);

  // Navigate to root — remove the folder param from the URL
  const navigateToRoot = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('folder');
      return next;
    });
  }, [setSearchParams]);

  // Fetch data when folder changes
  useEffect(() => {
    if (organization) {
      fetchForms(currentFolderId);
      fetchFolders(currentFolderId);
      fetchBreadcrumb(currentFolderId);
    }
  }, [organization, currentFolderId, fetchForms, fetchFolders, fetchBreadcrumb]);

  // Fetch analytics for all forms once forms are loaded
  useEffect(() => {
    if (forms.length === 0) return;
    const fetchAllAnalytics = async () => {
      const results = {};
      await Promise.all(
        forms.map(async (form) => {
          try {
            const res = await apiRequest(`/analytics/forms/${form.id}/summary`);
            if (res.ok) {
              results[form.id] = await res.json();
            }
          } catch {
            // Analytics unavailable for this form — skip silently
          }
        })
      );
      setFormAnalytics(results);
    };
    fetchAllAnalytics();
  }, [forms]);

  // Handle clicking outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openShareDropdown && shareDropdownRefs.current[openShareDropdown] &&
          !shareDropdownRefs.current[openShareDropdown].contains(event.target)) {
        setOpenShareDropdown(null);
      }
      if (openActionsDropdown && actionsDropdownRefs.current[openActionsDropdown] &&
          !actionsDropdownRefs.current[openActionsDropdown].contains(event.target)) {
        setOpenActionsDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openShareDropdown, openActionsDropdown]);

  const createForm = async (e) => {
    e.preventDefault();
    try {
      const response = await apiRequest('/forms/', {
        method: 'POST',
        body: JSON.stringify({
          title: newFormTitle,
          description: newFormDescription,
          fields: { steps: [] },
          settings: {},
          folder_id: currentFolderId || null,
        }),
      });
      const data = await response.json();
      setForms([...forms, data]);
      setShowCreateModal(false);
      setNewFormTitle('');
      setNewFormDescription('');
    } catch (error) {
      console.error('Error creating form:', error);
    }
  };

  const deleteForm = async (formId) => {
    if (window.confirm('Are you sure you want to delete this form?')) {
      try {
        await apiRequest(`/forms/${formId}`, {
          method: 'DELETE',
        });
        setForms((prevForms) => Array.isArray(prevForms) ? prevForms.filter(f => f.id !== formId) : []);
      } catch (error) {
        console.error('Error deleting form:', error);
      }
    }
  };

  const publishForm = async (formId) => {
    try {
      await apiRequest(`/forms/${formId}/publish`, {
        method: 'POST',
      });
      setForms((prevForms) =>
        Array.isArray(prevForms) ? prevForms.map(form =>
          form.id === formId ? { ...form, status: 'published' } : form
        ) : []
      );
      showSuccess('Form published successfully!');
    } catch (error) {
      console.error('Error publishing form:', error);
      showError('Failed to publish form');
    }
  };

  const unpublishForm = async (formId) => {
    try {
      await apiRequest(`/forms/${formId}/unpublish`, {
        method: 'POST',
      });
      setForms((prevForms) =>
        Array.isArray(prevForms) ? prevForms.map(form =>
          form.id === formId ? { ...form, status: 'draft' } : form
        ) : []
      );
      setOpenActionsDropdown(null);
      showSuccess('Form unpublished successfully!');
    } catch (error) {
      console.error('Error unpublishing form:', error);
      showError('Failed to unpublish form');
    }
  };

  const copyPublicLink = async (formId) => {
    const publicUrl = `${window.location.origin}/public/forms/${formId}`;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopiedFormId(formId);
      showSuccess('Public form link copied to clipboard!');
      setTimeout(() => setCopiedFormId(null), 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      showError('Failed to copy link');
    }
  };

  const getPublicUrl = useCallback((formId) => {
    return `${window.location.origin}/public/forms/${formId}`;
  }, []);

  const handleRowClick = useCallback((formId, event) => {
    if (event.target.closest('.action-button') || event.target.closest('.action-dropdown')) {
      return;
    }
    navigate(`/forms/${formId}/builder`);
  }, [navigate]);

  const toggleShareDropdown = useCallback((formId, event) => {
    event.stopPropagation();
    setOpenShareDropdown(prev => prev === formId ? null : formId);
  }, []);

  const toggleActionsDropdown = useCallback((formId, event) => {
    event.stopPropagation();
    setOpenActionsDropdown(prev => prev === formId ? null : formId);
  }, []);

  const duplicateForm = async (formId) => {
    try {
      const response = await apiRequest(`/forms/${formId}/duplicate`, {
        method: 'POST',
      });
      const data = await response.json();
      setForms([...forms, data]);
      setOpenActionsDropdown(null);

      setHighlightedFormId(data.id);

      setTimeout(() => {
        const element = document.querySelector(`[data-form-id="${data.id}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);

      setTimeout(() => setHighlightedFormId(null), 1500);

      showSuccess('Form duplicated successfully!');
    } catch (error) {
      console.error('Error duplicating form:', error);
      showError('Failed to duplicate form');
    }
  };

  // --- Folder operations ---

  const createFolder = async (name, parentId = null) => {
    try {
      const response = await apiRequest('/form-folders/', {
        method: 'POST',
        body: JSON.stringify({
          name,
          parent_id: parentId || currentFolderId || null,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setFolders((prev) => [...prev, data]);
        showSuccess('Folder created');
        return data;
      } else {
        showError('Failed to create folder');
      }
    } catch (error) {
      console.error('Error creating folder:', error);
      showError('Failed to create folder');
    }
    return null;
  };

  const renameFolder = async (folderId, newName) => {
    try {
      const response = await apiRequest(`/form-folders/${folderId}`, {
        method: 'PUT',
        body: JSON.stringify({ name: newName }),
      });
      if (response.ok) {
        const data = await response.json();
        setFolders((prev) =>
          prev.map((f) => (f.id === folderId ? data : f))
        );
        showSuccess('Folder renamed');
        return data;
      } else {
        showError('Failed to rename folder');
      }
    } catch (error) {
      console.error('Error renaming folder:', error);
      showError('Failed to rename folder');
    }
    return null;
  };

  const deleteFolder = async (folderId) => {
    if (window.confirm('Delete this folder? Forms and subfolders inside will be moved to the parent folder.')) {
      try {
        const response = await apiRequest(`/form-folders/${folderId}`, {
          method: 'DELETE',
        });
        if (response.ok) {
          // Refetch current view since forms/subfolders may have been moved
          fetchFolders(currentFolderId);
          fetchForms(currentFolderId);
          showSuccess('Folder deleted');
        } else {
          showError('Failed to delete folder');
        }
      } catch (error) {
        console.error('Error deleting folder:', error);
        showError('Failed to delete folder');
      }
    }
  };

  const renameForm = async (formId, newTitle) => {
    try {
      const response = await apiRequest(`/forms/${formId}`, {
        method: 'PUT',
        body: JSON.stringify({ title: newTitle }),
      });
      if (response.ok) {
        const data = await response.json();
        setForms((prevForms) =>
          Array.isArray(prevForms)
            ? prevForms.map((f) => (f.id === formId ? { ...f, title: data.title } : f))
            : []
        );
        setOpenActionsDropdown(null);
        showSuccess('Form renamed successfully!');
      } else {
        showError('Failed to rename form');
      }
    } catch (error) {
      console.error('Error renaming form:', error);
      showError('Failed to rename form');
    }
  };

  const moveFormToFolder = async (formId, targetFolderId) => {
    try {
      const response = await apiRequest(`/forms/${formId}/move`, {
        method: 'PUT',
        body: JSON.stringify({ folder_id: targetFolderId }),
      });
      if (response.ok) {
        // Remove form from current view
        setForms((prevForms) =>
          Array.isArray(prevForms) ? prevForms.filter((f) => f.id !== formId) : []
        );
        // Update folder counts
        fetchFolders(currentFolderId);
        showSuccess('Form moved successfully');
      } else {
        showError('Failed to move form');
      }
    } catch (error) {
      console.error('Error moving form:', error);
      showError('Failed to move form');
    }
  };

  // Create form from a template (called from the template gallery)
  const createFormFromTemplate = async (newForm) => {
    if (!newForm) return;
    setForms((prevForms) => [...prevForms, newForm]);
    setShowCreateModal(false);
    navigate(`/forms/${newForm.id}/builder`);
  };

  // Fetch all folders for the "move to folder" modal (flat list)
  const fetchAllFolders = useCallback(async () => {
    try {
      const response = await apiRequest('/form-folders/');
      if (response.ok) {
        const data = await response.json();
        return Array.isArray(data) ? data : [];
      }
    } catch (error) {
      console.error('Error fetching all folders:', error);
    }
    return [];
  }, []);

  return {
    // State
    forms,
    loading,
    formAnalytics,
    showCreateModal,
    newFormTitle,
    newFormDescription,
    copiedFormId,
    openShareDropdown,
    openActionsDropdown,
    highlightedFormId,
    shareDropdownRefs,
    actionsDropdownRefs,
    // Folder state
    folders,
    currentFolderId,
    breadcrumb,
    // Setters
    setShowCreateModal,
    setNewFormTitle,
    setNewFormDescription,
    setOpenShareDropdown,
    setOpenActionsDropdown,
    // Actions
    createForm,
    createFormFromTemplate,
    deleteForm,
    publishForm,
    unpublishForm,
    copyPublicLink,
    getPublicUrl,
    handleRowClick,
    toggleShareDropdown,
    toggleActionsDropdown,
    duplicateForm,
    renameForm,
    // Folder actions
    createFolder,
    renameFolder,
    deleteFolder,
    moveFormToFolder,
    navigateToFolder,
    navigateToRoot,
    fetchAllFolders,
  };
}
